import { memo } from "react";
import {
  Button,
  Dropdown,
  Flex,
  Heading,
  Icon,
  IconName,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  ErrorCard,
} from "@repo/ping-design";
import {
  PingLoading,
  BuildingPanelTopData,
  getPliLink,
  CollapsibleSection,
  BuildingPanelSection,
  PolicyBuildingType,
  SOURCES,
} from "@repo/ping-react-js";
import { TopSection } from "./sections/TopSection";
import { AoASection } from "./sections/AoASection";
import { CopeSection } from "./sections/CopeSection";
import { DisplacementSection } from "./sections/DisplacementSection";

import "./BuildingsPanel.scss";

type BuildingsPanelProps = {
  sovid: string | null;
  isFetching: boolean;
  policy: any;
  clientLayerSettings: any;
  buildingTitle: BuildingPanelTopData;
  geocodingMatchLabel: string | null;
  hasDisplacement: boolean;
  displacementSectionData: Array<{ k: string; v: string | number }> | null;
  locationPanelLatLng: { lat: number; lng: number } | null;
  sideSectionData: BuildingPanelSection;
  copeSectionData: BuildingPanelSection;
  policyInitialData: PolicyBuildingType;
  pingHazardOptions: { label: string; id: string }[];
  otherOptions: { label: string; id: string }[];
  getExternalMapProviderOptions: (
    address: string,
  ) => { label: string; value: string }[];
  handleLinkClicked: (
    address: string | Record<string, string | number>,
  ) => void;
  getGeocodingIconName: (matchLevel: string) => IconName;
  buttonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | HTMLAnchorElement | null>
  >;
  error: { source: string; message: string } | null;
  setError: (error: { source: string; message: string } | null) => void;
  aoaIntegrations: {
    E2V: any;
    PHAZ: any;
    OTH: any;
  };
  aoaInputOptions?: Record<
    string,
    Record<string, Array<{ label: string; value: string | number }>>
  >;
  canAccessDeveloperTools: boolean;
  geocodingMode: boolean;
  displacementMode: boolean;
  onClickDisplacement?: () => void;
  onClearActivePanel?: () => void;
  /** Hide the inline close button when dismissal is handled by an outer container (e.g. a mobile sheet). */
  showCloseButton?: boolean;
  onGeocodingToggled?: () => void;
  isReadOnly?: boolean;
  isInternationalLocationError?: boolean;

  buildingId: string | null;

  triggerAoAQuery: (args: any) => Promise<any>;
  isSourceFetching: (source: string) => boolean;
  onResetAoASection?: (sectionId: string) => void;
  onClearAoASection?: () => void;
  onSetAoAResponse?: (data: any) => void;
  copeSectionsAmt: number;
};

export const BuildingsPanel = memo(
  ({
    sovid,
    policy,
    isFetching,
    canAccessDeveloperTools,
    clientLayerSettings,
    buildingTitle,
    policyInitialData,
    locationPanelLatLng,
    sideSectionData,
    copeSectionData,
    displacementSectionData,
    pingHazardOptions,
    otherOptions,
    geocodingMatchLabel,
    geocodingMode,
    displacementMode,
    getGeocodingIconName,
    getExternalMapProviderOptions,
    handleLinkClicked,
    buttonRefs,
    error,
    setError,
    aoaIntegrations,
    aoaInputOptions,
    hasDisplacement,
    onClearActivePanel,
    showCloseButton = true,
    onClickDisplacement,
    onGeocodingToggled,

    isReadOnly,
    isInternationalLocationError,
    onResetAoASection,
    onClearAoASection,

    isSourceFetching,
    triggerAoAQuery,
    buildingId,
    onSetAoAResponse,
    copeSectionsAmt,
  }: BuildingsPanelProps) => {
    if (isFetching || !policy || !clientLayerSettings) {
      return (
        <div className="BuildingsPanel__LoadingContainer">
          <PingLoading className="BuildingsPanel__Loading" />
        </div>
      );
    }

    const e2ValueOptions = otherOptions.filter(
      (opt) => opt.id === SOURCES.E2VALUE,
    );
    const otherOptionsWithoutE2Value = otherOptions.filter(
      (opt) => opt.id !== SOURCES.E2VALUE,
    );

    return (
      <div className="BuildingsPanel">
        <Flex
          direction="column"
          gap="5"
          className="BuildingsPanel__TitleSection"
        >
          <Flex direction="row" justify="space-between">
            <Flex direction="column" gap="1-5">
              {buildingTitle.buildingName != "No Building Name" && (
                <Heading size="lg">{buildingTitle.buildingName}</Heading>
              )}

              <Flex direction="column" gap="2">
                {!buildingTitle?.constructedAddress && (
                  <Tooltip placement="left">
                    <TooltipTrigger>
                      <Flex
                        className="BuildingsPanel__Descriptor__Main"
                        gap="1"
                        align="center"
                      >
                        <Dropdown
                          customTrigger={
                            <Icon
                              className="BuildingsPanel__Descriptor__Icon"
                              iconName={getGeocodingIconName(
                                buildingTitle.matchLevel ?? "",
                              )}
                              onClick={(e: React.MouseEvent) => {
                                window.open(
                                  getPliLink(buildingTitle.buildingPingAddress),
                                  "_blank",
                                );
                              }}
                            />
                          }
                          options={getExternalMapProviderOptions(
                            buildingTitle.buildingPingAddress,
                          )}
                          variant="translucent"
                          onChange={(selected: any) =>
                            handleLinkClicked(selected.value)
                          }
                          dropdownWidth={140}
                        />{" "}
                        {buildingTitle.buildingPingAddress}
                      </Flex>
                    </TooltipTrigger>
                    <TooltipContent>Ping Geocoded Location</TooltipContent>
                  </Tooltip>
                )}

                <Tooltip placement="left">
                  <TooltipTrigger>
                    <Flex className="BuildingsPanel__Descriptor__Sub" gap="1">
                      <Dropdown
                        customTrigger={
                          <Icon
                            iconName="AccuracyOriginalPing"
                            onClick={(e: React.MouseEvent) => {
                              window.open(
                                getPliLink(
                                  buildingTitle?.constructedAddress
                                    ? buildingTitle.constructedAddress
                                    : buildingTitle.buildingOriginalAddress,
                                ),
                                "_blank",
                              );
                            }}
                          />
                        }
                        options={getExternalMapProviderOptions(
                          buildingTitle?.constructedAddress
                            ? buildingTitle.constructedAddress
                            : buildingTitle.buildingOriginalAddress,
                        )}
                        variant="translucent"
                        onChange={(selected: any) =>
                          handleLinkClicked(selected.value)
                        }
                        dropdownWidth={140}
                      />{" "}
                      {buildingTitle?.constructedAddress
                        ? buildingTitle.constructedAddress
                        : buildingTitle.buildingOriginalAddress}
                    </Flex>
                  </TooltipTrigger>
                  <TooltipContent>Originally Submitted Address</TooltipContent>
                </Tooltip>
              </Flex>
            </Flex>
            {showCloseButton && (
              <Button
                size="sm"
                iconName="X"
                variant="outline"
                onClick={() => {
                  onClearActivePanel?.();
                }}
              />
            )}
          </Flex>
        </Flex>

        <Flex className="BuildingsPanel__Sections" direction="column" gap="2">
          <Flex
            className="BuildingsPanel__TitleControls"
            direction="row"
            justify="space-between"
            align="center"
          >
            {geocodingMatchLabel ? (
              <Flex direction="column">
                <span className="BuildingsPanel__TitleControls__AccuracyTitle">
                  Geocoding Accuracy
                </span>
                <span className="BuildingsPanel__TitleControls__AccuracyDescription">
                  {geocodingMatchLabel}
                </span>
              </Flex>
            ) : canAccessDeveloperTools ? (
              <></>
            ) : // <PingDownloadPDFButton />
            null}

            {onGeocodingToggled && !isReadOnly && (
              <Switch
                label="Show Geocoding"
                checked={geocodingMode}
                onChange={() => {
                  onGeocodingToggled?.();
                }}
              />
            )}
          </Flex>

          {hasDisplacement && onClickDisplacement && (
            <CollapsibleSection title={"Lat / Lng"}>
              <DisplacementSection
                section={displacementSectionData ?? []}
                isDisplacementMode={displacementMode}
                onClickDisplacement={onClickDisplacement}
              />
            </CollapsibleSection>
          )}

          {sideSectionData.values.length > 0 && (
            <CollapsibleSection
              title={sideSectionData.title}
              isInternationalLocationError={isInternationalLocationError}
            >
              <TopSection
                latLng={locationPanelLatLng ?? { lat: 0, lng: 0 }}
                section={sideSectionData}
              />
            </CollapsibleSection>
          )}

          <CollapsibleSection
            title="COPE"
            isInternationalLocationError={isInternationalLocationError}
          >
            {error && (
              <div className="AoASection">
                <div className="AoASection__ErrorContainer">
                  <ErrorCard
                    variant="default"
                    title="API error"
                    error={error.message}
                    onClose={() => setError(null)}
                  />
                </div>
              </div>
            )}
            <CopeSection
              section={copeSectionData}
              copeSectionsAmt={copeSectionsAmt}
              isPLI={false}
            />
          </CollapsibleSection>

          {e2ValueOptions.length > 0 && (
            <AoASection
              sovid={sovid}
              aoaIntegrations={aoaIntegrations}
              options={e2ValueOptions}
              policyBuildingData={policyInitialData}
              requiredFields={clientLayerSettings?.pingmaps?.required_fields}
              integrationSettings={clientLayerSettings?.pingmaps?.integrations}
              aoaInputOptions={aoaInputOptions}
              buttonRefs={buttonRefs}
              isReadOnly={isReadOnly}
              buildingId={buildingId}
              isInternationalLocationError={isInternationalLocationError}
              onResetAoASection={(sectionId: string) => {
                onResetAoASection?.(sectionId);
              }}
              onClearAoASection={() => {
                onClearAoASection?.();
              }}
              onSetAoAResponse={onSetAoAResponse}
              triggerAoAQuery={triggerAoAQuery}
              isSourceFetching={isSourceFetching}
            />
          )}

          <AoASection
            sovid={sovid}
            aoaIntegrations={aoaIntegrations}
            options={pingHazardOptions}
            policyBuildingData={policyInitialData}
            requiredFields={clientLayerSettings?.pingmaps?.required_fields}
            integrationSettings={clientLayerSettings?.pingmaps?.integrations}
            aoaInputOptions={aoaInputOptions}
            buttonRefs={buttonRefs}
            isReadOnly={isReadOnly ?? false}
            buildingId={buildingId}
            isInternationalLocationError={isInternationalLocationError}
            onResetAoASection={(sectionId: string) => {
              onResetAoASection?.(sectionId);
            }}
            onClearAoASection={() => {
              onClearAoASection?.();
            }}
            onSetAoAResponse={onSetAoAResponse}
            triggerAoAQuery={triggerAoAQuery}
            isSourceFetching={isSourceFetching}
          />

          <AoASection
            sovid={sovid}
            aoaIntegrations={aoaIntegrations}
            options={otherOptionsWithoutE2Value}
            policyBuildingData={policyInitialData}
            requiredFields={clientLayerSettings?.pingmaps?.required_fields}
            integrationSettings={clientLayerSettings?.pingmaps?.integrations}
            aoaInputOptions={aoaInputOptions}
            buttonRefs={buttonRefs}
            isReadOnly={isReadOnly ?? false}
            buildingId={buildingId}
            isInternationalLocationError={isInternationalLocationError}
            onResetAoASection={(sectionId: string) => {
              onResetAoASection?.(sectionId);
            }}
            onClearAoASection={() => {
              onClearAoASection?.();
            }}
            onSetAoAResponse={onSetAoAResponse}
            triggerAoAQuery={triggerAoAQuery}
            isSourceFetching={isSourceFetching}
          />
        </Flex>
      </div>
    );
  },
);
